import React, { useEffect, useState } from "react";

import {
  Box,
  TextField,
  Paper,
  Typography,
  IconButton,
  Stack,
  InputAdornment,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";

import { Edit, Save, Cancel, Search } from "@mui/icons-material";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";

import { ptPT } from "@mui/x-data-grid/locales";

import { getProducts, updateProducts } from "../../services/source";

const Produtos = ({ refreshProducts }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [imageOpen, setImageOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedCode, setSelectedCode] = useState("");

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await getProducts();
      setProducts(data);
      setFilteredProducts(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const value = search.toLowerCase();
    setFilteredProducts(
      products.filter(
        (p) =>
          p.title?.toLowerCase().includes(value) ||
          p.product_code?.toLowerCase().includes(value) ||
          p.location?.toLowerCase().includes(value),
      ),
    );
  }, [search, products]);

  const handleEdit = (row) => {
    setEditingId(row.id);
    setEditData({
      title: row.title,
      subtitle: row.subtitle,
      price: row.price,
      stock: row.stock,
      location: row.location,
    });
  };

  const handleSave = async (id) => {
    try {
      const response = await updateProducts({
        id,
        ...editData,
      });

      if (response.data.success) {
        await refreshProducts();

        setProducts((prev) =>
          prev.map((p) =>
            p.id === id
              ? {
                  ...p,
                  ...editData,
                }
              : p,
          ),
        );

        setEditingId(null);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const inputChange = (field, value) => {
    setEditData({
      ...editData,

      [field]: value,
    });
  };

  const EditField = ({ field, type = "text" }) => (
    <TextField
      size="small"
      fullWidth
      type={type}
      value={editData[field] || ""}
      onChange={(e) => inputChange(field, e.target.value)}
    />
  );

  const handleOpenImage = (row) => {
    setSelectedCode(row.product_code);

    setSelectedImage(`${process.env.REACT_APP_API_URL}/${row.image}`);

    setImageOpen(true);
  };

  const columns = [
    {
      field: "title",
      headerName: "Produto",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <EditField field="title" />
        ) : (
          <Typography fontWeight={600}>{params.value}</Typography>
        ),
    },

    {
      field: "subtitle",
      headerName: "Subtítulo",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <EditField field="subtitle" />
        ) : (
          params.value
        ),
    },
    {
      field: "product_code",
      headerName: "Código",
      width: 180,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            color="primary"
            sx={{
              cursor: "pointer",
              fontWeight: 500,
            }}
            onClick={() => handleOpenImage(params.row)}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },

    {
      field: "price",
      headerName: "Preço",
      width: 120,

      renderCell: (params) =>
        editingId === params.row.id ? (
          <EditField field="price" type="number" />
        ) : (
          <Chip
            label={`${Number(params.value).toLocaleString()} ${
              params.row.location?.toLowerCase().includes("lisboa") ? "€" : "Kz"
            }`}
            color="primary"
            size="small"
          />
        ),
    },

    {
      field: "stock",
      headerName: "Stock",
      width: 100,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <EditField field="stock" type="number" />
        ) : (
          <Chip
            label={params.value}
            color={params.value > 0 ? "success" : "error"}
            size="small"
          />
        ),
    },

    {
      field: "location",
      headerName: "Localização",
      width: 150,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <EditField field="location" />
        ) : (
          params.value
        ),
    },

    {
      field: "actions",
      headerName: "Ações",
      width: 130,
      sortable: false,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <Stack direction="row">
            <IconButton
              color="success"
              onClick={() => handleSave(params.row.id)}
            >
              <Save />
            </IconButton>

            <IconButton color="error" onClick={() => setEditingId(null)}>
              <Cancel />
            </IconButton>
          </Stack>
        ) : (
          <IconButton color="primary" onClick={() => handleEdit(params.row)}>
            <Edit />
          </IconButton>
        ),
    },
  ];

  return (
    <Box
      sx={{
        maxWidth: 1500,
        mx: "auto",

        p: {
          xs: 1,
          md: 3,
        },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: {
            xs: 2,
            md: 3,
          },

          borderRadius: 3,
        }}
      >
        <Typography variant="h5" fontWeight={700} mb={2}>
          Gestão de Produtos
        </Typography>

        <TextField
          fullWidth
          label="Pesquisar produto"
          placeholder="Código, produto ou localização"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,

            maxWidth: 600,
          }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",

              overflow: "auto",
            }}
          >
            <DataGrid
              rows={filteredProducts}
              columns={columns}
              getRowId={(row) => row.id}
              autoHeight
              slots={{
                toolbar: GridToolbar,
              }}
              localeText={ptPT.components.MuiDataGrid.defaultProps.localeText}
              pageSizeOptions={[10, 25, 50]}
              sx={{
                border: 0,

                "& .MuiDataGrid-columnHeaders": {
                  background: "#f5f5f5",
                  fontWeight: 700,
                },

                // Header text aligned left
                "& .MuiDataGrid-columnHeaderTitle": {
                  width: "100%",
                  textAlign: "left",
                  fontWeight: "bold",
                },

                // Header content aligned left
                "& .MuiDataGrid-columnHeader": {
                  justifyContent: "flex-start",
                },

                // Cell content aligned left
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  textAlign: "left",
                },

                "& .MuiDataGrid-row:hover": {
                  background: "#fafafa",
                },
              }}
            />
          </Box>
        )}
      </Paper>
      <Dialog
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCode}

          <IconButton
            onClick={() => setImageOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <Cancel />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={selectedImage}
              alt={selectedCode}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
              onError={(e) => {
                console.log("Image error:", selectedImage);
                e.target.style.display = "none";
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Produtos;
